import {
  StateDefinitions,
  TypeOfStateDeclaration,
} from '../storage/state-declaration.js';
import { EventDeclarations } from '../event-store/event-declarations.js';
import { OutputDeclarations } from '../stream/output-declarations.js';
import { HandleDeclarations } from '../stream/handle-declarations.js';
import { HostRuntimeContext, HostRuntimeExecution } from './host-runtime.js';
import {
  EventContextSnapshotClient,
  EventContextStorage,
  EventHandleContext,
  EventHandleStateClient,
} from './runtime.js';
import { StoredEvent } from '../event-store/stored-event.js';
import { EventRandomSeed } from '../event-store/event-random-seed.js';
import { RuntimeEnv } from '../environment/runtime-env.js';
import { RuntimeResult } from './runtime-result.js';
import { hostResultBuilder } from './host-result-builder.js';
import { DeleteType } from '../storage/delete-type.js';
import { PatchType } from '../storage/patch-type.js';
import { UpsertType } from '../storage/upsert-type.js';
import { createEventContextStorage } from './create-event-context-storage.js';
import { getOrFail } from '../utils/get-or-fail.js';
import { deserializeData } from './get-state-data-keys.js';
import { getErrorMessage, getErrorStackTrace } from './get-error-message.js';
import { Telemetry } from '../tracer/telemetry.js';

export function createHostRuntime<
  TState extends StateDefinitions,
  TEvents extends EventDeclarations,
  TOutputs extends OutputDeclarations,
>(
  events: TEvents,
  handles: HandleDeclarations<TState, TEvents, TOutputs>,
  outputs: TOutputs,
): HostRuntimeExecution<TState> {
  return {
    async execute(
      context: HostRuntimeContext,
      state: EventContextStorage<TState>,
      evt: StoredEvent,
      random: EventRandomSeed,
      env: RuntimeEnv,
      telemetry: Telemetry,
      signal: AbortSignal,
    ): Promise<RuntimeResult> {
      try {
        const resultBuilder = hostResultBuilder(outputs, evt);

        const ctx: EventHandleContext<any, any, any> = {
          env,
          signal,
          stream: context,
          emit<K extends string>(type: K, data: any): Promise<void> {
            resultBuilder.addOutput(type, data);
            return Promise.resolve();
          },
          state<K extends string>(name: K): EventHandleStateClient<TState[K]> {
            return {
              async delete(id: DeleteType<TState[K]>): Promise<void> {
                const { keys } = await state.state(name).delete(id);
                resultBuilder.addOperation({
                  type: 'delete',
                  state: name,
                  keys,
                });
              },
              async patch(val: PatchType<TState[K]>): Promise<void> {
                const { data, keys } = await state.state(name).patch(val);
                resultBuilder.addOperation({
                  type: 'patch',
                  state: name,
                  data,
                  keys,
                });
              },
              async insert(
                val: TypeOfStateDeclaration<TState[K]>,
              ): Promise<void> {
                const { data, keys } = await state.state(name).insert(val);
                resultBuilder.addOperation({
                  type: 'insert',
                  state: name,
                  data,
                  keys,
                });
              },
              async upsert(val: UpsertType<TState[K]>): Promise<void> {
                const { data, keys } = await state.state(name).upsert(val);
                resultBuilder.addOperation({
                  type: 'upsert',
                  state: name,
                  data,
                  keys,
                });
              },
            };
          },
          snapshot<K extends string & keyof TState>(
            name: K,
          ): EventContextSnapshotClient<TState[K]> {
            return createEventContextStorage(state.snapshot(name), signal);
          },
          random,
          metadata: {
            id: evt.id,
            auth: evt.auth,
            createdAt: evt.createdAt,
            storedAt: evt.storedAt,
            annotations: evt.annotations,
          },
        };

        const handle = getOrFail(handles, evt.event);
        const eventStruct = getOrFail(events, evt.event);
        const eventData = deserializeData(
          eventStruct.type,
          evt.data,
          telemetry,
        );
        await handle(eventData, ctx);

        return resultBuilder.get();
      } catch (e) {
        return {
          eventId: evt.id,
          success: false,
          error: getErrorMessage(e),
          stacktrace: getErrorStackTrace(e),
        };
      }
    },
  };
}
